import {
  LessonData,
  OpenQuestionTeacherPinSettings,
  PinTeacherSettings,
  PinType,
  TeacherPin,
} from '@lessonup/pins-shared';
import { get, has, isArray } from 'lodash';
import { TeacherEntry } from '../entries/TeacherEntry';
import { AppError } from '../errors/AppError';
import { Assignment } from './Assignment';
import { AssignmentBasicInfo } from './AssignmentBasicInfo';
import { AssignmentContent } from './AssignmentContent';
import { AssignmentData } from './AssignmentData';
import { AssignmentMeta } from './AssignmentMeta';
import { AssignmentSettings, AssignmentSettingsType } from './AssignmentSettings';
import { AssignmentUtils } from './AssignmentUtils';

export type AssignmentType = 'realtime';
/**
 * Dit zou AssignmentData uiteindelijk moeten vervangen, maar voor nu zit dit voornamelijk in de database laag en bij het aanmaken / updaten van assignments
 */
export interface AssignmentComplete<Type extends AssignmentType = AssignmentType> {
  _id: string;
  type: Type;
  content: AssignmentContent<Type>;
  meta: AssignmentMeta;
  settings: AssignmentSettingsType<Type>;
  teacherEntry: TeacherEntry | undefined;
}

export namespace AssignmentComplete {
  export type Realtime = AssignmentComplete<'realtime'>;

  export function isRealtime(data: AssignmentComplete): data is AssignmentComplete.Realtime {
    return data.type === 'realtime';
  }

  export function from(
    content: AssignmentContent | undefined,
    meta: AssignmentMeta | undefined,
    settings: AssignmentSettings | undefined,
    teacherEntry: TeacherEntry | undefined
  ): AssignmentComplete | undefined {
    if (!content || !meta || !settings) {
      return undefined;
    }

    return {
      _id: meta._id,
      type: meta.type,
      content,
      meta,
      settings,
      teacherEntry,
    };
  }

  export function asAssignmentData(data: AssignmentComplete): AssignmentData {
    return {
      assignment: Assignment.from(data.content, data.settings),
      meta: data.meta,
    };
  }
}

export namespace AssignmentComplete {
  const manualAnswerKeys = ['manuallyApprovedAnswers', 'manuallyRejectedAnswers'] as const;

  type ManualAnswerKey = (typeof manualAnswerKeys)[number];

  function hasManualAnswers<Key extends ManualAnswerKey>(
    settings: PinTeacherSettings<PinType> | undefined,
    key: Key
  ): settings is OpenQuestionTeacherPinSettings & { [K in Key]: string[] } {
    if (!has(settings, key)) {
      return false;
    }
    const value = get(settings, key);
    return isArray(value);
  }

  function preserveManualAnswersSettings(
    oldSettings: { [pinId: string]: PinTeacherSettings<PinType> },
    newSettings: { [pinId: string]: PinTeacherSettings<PinType> }
  ): { [pinId: string]: PinTeacherSettings<PinType> } {
    const preserved = { ...newSettings };

    for (const pinId in oldSettings) {
      const oldPinSettings = oldSettings[pinId];
      const preservedPinSettings = preserved[pinId];

      if (!preservedPinSettings) {
        continue;
      }

      let updatedPinSettings = preservedPinSettings;

      for (const key of manualAnswerKeys) {
        if (!hasManualAnswers(oldPinSettings, key)) {
          continue;
        }

        const manualAnswers = oldPinSettings[key];
        if (manualAnswers.length === 0) {
          continue;
        }

        updatedPinSettings = {
          ...updatedPinSettings,
          [key]: manualAnswers,
        };
      }

      preserved[pinId] = updatedPinSettings;
    }

    return preserved;
  }

  export function createFromLesson<Type extends AssignmentType = AssignmentType>(
    type: Type,
    assignmentId: string,
    lessonData: LessonData,
    options: AssignmentContent.Options,
    creationSettings: AssignmentSettings.CreationSettingsTyped<Type>,
    ownerId: string,
    teachers: string[]
  ): AssignmentComplete<Type> {
    const createdAt = Date.now();

    const content = AssignmentContent.createFromLesson<Type>(
      assignmentId,
      ownerId,
      type,
      lessonData,
      createdAt,
      options
    );
    const settings = AssignmentSettings.create<Type>(assignmentId, ownerId, createdAt, creationSettings);
    const teacherEntry = TeacherEntry.create(assignmentId, ownerId);

    const meta = AssignmentMeta.createFromLesson(
      assignmentId,
      creationSettings.type,
      ownerId,
      createdAt,
      teachers,
      AssignmentBasicInfo.create(content),
      lessonData,
      options
    );

    return { content, meta, settings, type, _id: assignmentId, teacherEntry };
  }

  /** creates a new object with updated contents */
  export function updateWithLesson<T extends AssignmentType = AssignmentType>(
    type: T,
    old: AssignmentComplete<T>,
    lessonData: LessonData,
    options: AssignmentContent.Options,
    creationSettings: AssignmentSettings.CreationSettingsTyped<T>
  ): AssignmentComplete<T> {
    const oldContent = old.content;
    const oldMeta = old.meta;
    const oldTeachers = old.meta.usersWithAccess;
    const oldTeacherEntry = old.teacherEntry;

    const updated = createFromLesson(
      type,
      old.meta._id,
      lessonData,
      options,
      creationSettings,
      old.meta.ownerId,
      oldTeachers
    );

    const preservedTeacherSettings = preserveManualAnswersSettings(
      oldMeta.teacherSettings,
      updated.meta.teacherSettings
    );

    const settings: AssignmentSettingsType<T> = {
      ...updated.settings,
      status: old.settings.status,
      visibility: old.settings.visibility,
    };
    return {
      _id: old._id,
      type: old.type,
      content: AssignmentContent.updateFromLesson(oldContent, lessonData, options),
      settings,
      meta: {
        ...updated.meta,
        createdAt: oldMeta.createdAt,
        teacherSettings: preservedTeacherSettings,
      },
      teacherEntry: oldTeacherEntry,
    };
  }

  export function createRealtimeAssignment(
    assignmentId: string,
    pincode: string,
    lessonData: LessonData,
    preferredPin: TeacherPin | undefined,
    options: AssignmentContent.Options,
    ownerId: string,
    ignoreExclusions: boolean,
    pincodeClosed: boolean | undefined,
    allowConnectedDevices: boolean,
    autoShowRank: boolean,
    isSoundEnabled: boolean
  ): AssignmentComplete<'realtime'> {
    const pin = preferredPin || AssignmentUtils.firstPinForAssignmentType(lessonData.pins, 'realtime');

    if (!pin) {
      throw new AppError('not-found', `Missing pin for lesson`, { lessonId: lessonData.lesson._id });
    }

    const playerSettings: AssignmentSettings.RealtimePlayerSettings = {
      allowConnectedDevices,
      isScreenShared: false,
      isPrepScreenEnabled: true,
      allowNewPlayers: !pincodeClosed,
      isSoundEnabled,
      autoShowRank,
      ignoreExclusions,
    };

    const settings: AssignmentSettings.RealtimeCreationSettings = {
      type: 'realtime',
      visibility: 'all',
      status: 'open',
      nextAssignmentId: undefined,
      pincode,
      playerSettings,
      currentView: {
        pinId: pin.id,
        foregroundPinId: pin.id,
        hasNext: undefined,
        hasPrevious: undefined,
        progress: undefined,
        params: undefined, // always start without params
      },
    };

    const data = AssignmentComplete.createFromLesson('realtime', assignmentId, lessonData, options, settings, ownerId, [
      ownerId,
    ]);
    return data;
  }
}
